import React, { useEffect, useState } from "react"

import { App, Modal, Spin, Tooltip, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import CreditRequestForm from "../../../../Components/Forms/CreditRequestForm"
import withPermission from "../../../../hoc/withPermission"
import Icon from "../../../../Icon"
import { extendedAccessRoles } from "../../../../Utils/permission"
import { creditRequestOptions } from "../../../../Utils/utils"
import { CreditRequestInstructions } from "../../../CreditsPage/components/CreditRequestInstructions"
import CreditCreators from "../../../CreditsPage/reducer"
import { useCreditsPageQuery } from "../../../CreditsPage/useCreditsPageQuery"
import { getRemainingTime } from "../../../DashboardPage/fn"
import { alertsActions } from "../../redux/alerts.slice"
import { createCreditRequest, getAlertDetails } from "../../redux/alerts.thunks"

import instructionsStyles from "../../../CreditsPage/components/CreditRequestInstructions/CreditRequestInstructions.module.less"

const { Text } = Typography

const conditionalFields = {
  credit_request_details: {
    placeholder: "Enter any additional or related information to this credit request...",
    tooltip:
      "Provide an extended comment explaining the reason for requesting credits. If you selected “Other” in the previous field, additional details are required to proceed.",
    required: { request_reason: "Other" }
  },
  request_reason: {
    options: creditRequestOptions,
    title: "Reason for credit request"
  },
  validate: ["request_reason"],
  isAttachDisabled: false
}

const additionalFields = { request_type: {}, transaction_id: {} }

const PermissionButtonExtended = withPermission(ButtonComponent, extendedAccessRoles)

export const CreditRequestModal = ({
  onlyModal = false,
  isDemo,
  tourElId,
  alert_id,
  showIcon = true,
  fullWidth = false
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const { notification } = App.useApp()
  const isMobile = useSelector((state) => state.nav.isMobile)

  const { t } = useTranslation()
  const { openCreditResubmit } = useCreditsPageQuery(search)
  const { alertDetails, saveFetching, saveResult, creditRequestSubmitting } = useSelector(
    (state) => state.alerts
  )
  const {
    creditDetails,
    saveResult: creditSaveResult,
    fetching
  } = useSelector((state) => state.credits)
  const businessAccount = useSelector((state) => state.user.businessAccount)

  const baseDataDetails = openCreditResubmit ? creditDetails : alertDetails

  const creditRequestId = baseDataDetails?.credit_request_id
  const isNewRequest = !creditRequestId && !openCreditResubmit

  const isEnableForResubmit = baseDataDetails?.enabled_for_resubmit?.status
  const exceededResubmitAttempts =
    (baseDataDetails?.enabled_for_resubmit?.resubmit_attempts ?? 0) > 3
  const baRole = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const baRoleName = useSelector((state) => state.auth.userInfo?.ba_role?.name)
  const role = useSelector((state) => state.auth.userInfo?.role?.id)
  const currentBusinessAccountId = useSelector(
    (state) => state.auth.userInfo?.current_business_account_id
  )
  const isAdmin = useSelector((state) => state.auth.isAdmin)
  const [open, setOpen] = useState(false)
  const [selectedReason, setSelectedReason] = useState(null)
  const isBusinessAccountAdministrator = baRoleName === "Administrator"

  const { days } = getRemainingTime(baseDataDetails?.alert_timestamp)

  const maxDaysOld = baseDataDetails?.alert_service === "ethoca" ? 30 : 20
  const isAlertOlderThanLimit = -days > maxDaysOld && isNewRequest

  const modalVisible = (openCreditResubmit && !!creditDetails) || open

  const isDisabledSubmitBtn =
    (!extendedAccessRoles?.includes(baRole ?? role) && !isAdmin) ||
    (isDemo && !isBusinessAccountAdministrator)
  const isDemoModeBlocked = !currentBusinessAccountId && !isAdmin

  const alertStatusToRequestReason = {
    "Not found": "Transaction not found",
    "Failed transaction": "Alert went to failed/not authenticated transaction",
    "Previously refunded": "Transaction was refunded before the alert",
    "Already a chargeback": "Transaction became a chargeback before the alert"
  }
  const rawInitialValues = creditRequestId || openCreditResubmit ? creditDetails : alertDetails
  const formInitialValues =
    isNewRequest && alertStatusToRequestReason[rawInitialValues?.alert_status]
      ? {
          ...rawInitialValues,
          request_reason: alertStatusToRequestReason[rawInitialValues?.alert_status]
        }
      : rawInitialValues

  const connectedPaymentProcessors = businessAccount?.connected_payment_processors
  const getInstructionProcessor = (processors) => {
    if (processors?.length === 1 && processors[0] === "stripe") return "stripe"
    if (processors?.length === 1 && processors[0] === "shopify") return "shopify"
    return "other"
  }
  const instructionProcessor = getInstructionProcessor(connectedPaymentProcessors)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (creditRequestId) {
      dispatch(CreditCreators.getCreditDetailsRequest(creditRequestId))
    }
  }, [creditRequestId])

  useEffect(() => {
    if (saveResult === "attach_proof") {
      notification.success({
        message: t("Credit request updated"),
        description: t(
          "Thank you for reaching out! Your credit request has been updated successfully."
        ),
        ...notificationConfig
      })
    } else if (saveResult === "credit_request_create") {
      notification.success({
        message: t("Credit request submitted."),
        description: t(
          "Thank you for reaching out! Your credit request has been submitted successfully."
        ),
        ...notificationConfig
      })
    } else if (creditSaveResult === "credit_request_resubmit") {
      notification.success({
        message: t("Credit request resubmitted."),
        description: t(
          "Thank you for reaching out! Your credit request has been resubmitted successfully."
        ),
        ...notificationConfig
      })
    }
    handleCloseModal()
    dispatch(alertsActions.resetSaveResult())
    dispatch(CreditCreators.resetSaveResult())
  }, [saveResult, creditSaveResult])

  const handleCloseModal = () => {
    if (openCreditResubmit) {
      navigate(pathname, { replace: true })
    } else {
      setOpen(false)
    }
    if (onlyModal) dispatch(CreditCreators.creditDetailsReset())
  }

  const updateAlertDetails = () => {
    if (alert_id) {
      dispatch(getAlertDetails(alert_id))
    }
  }

  const handleSubmitForm = (data) => {
    if (isEnableForResubmit) {
      dispatch(
        CreditCreators.resubmitCreditRequestRequest({
          id: baseDataDetails?.id ?? baseDataDetails?.credit_request_id,
          data,
          onSuccess() {
            updateAlertDetails()
          }
        })
      )
    } else {
      // The legacy action carried an `onSuccess` callback in its payload; the thunk reports
      // the same outcome through its promise instead.
      dispatch(
        createCreditRequest({
          id: baseDataDetails?.alert_id,
          data
        })
      )
        .unwrap()
        .then(updateAlertDetails)
        .catch(() => {})
    }
  }

  const tooltipText = (alertService) => {
    switch (true) {
      case exceededResubmitAttempts:
        return t("Maximum resubmit attempts exceeded")
      case baseDataDetails?.enabled_for_resubmit?.reason !== null && !isNewRequest:
        return baseDataDetails?.enabled_for_resubmit?.reason
      case isAlertOlderThanLimit:
        return alertService === "ethoca"
          ? t("Credits may be requested for alerts not older than 30 days")
          : t("Credits may be requested for alerts not older than 20 days")
      default:
        return null
    }
  }

  const Button = isAdmin ? ButtonComponent : PermissionButtonExtended

  return (
    <>
      {onlyModal ? null : (
        <Tooltip title={tooltipText(alertDetails?.alert_service)}>
          <Button
            onClick={() => setOpen(true)}
            icon={showIcon ? <Icon type="arrow_share_right" /> : null}
            text={t("Credit request")}
            style={{ width: fullWidth && "100%" }}
            data-tour="credit-request-btn"
            role={baRole ?? role}
            disabled={
              (!baseDataDetails?.enabled_for_request && !isEnableForResubmit) ||
              isAlertOlderThanLimit ||
              (isDemo && !isBusinessAccountAdministrator) ||
              exceededResubmitAttempts ||
              isDemoModeBlocked
            }
          />
        </Tooltip>
      )}
      <Modal
        open={modalVisible}
        centered={!isMobile}
        width={isMobile ? 442 : 842}
        title={isMobile ? "Credit request" : null}
        footer={null}
        closable={isMobile}
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={{ content: cls("clear-bg", !isMobile && instructionsStyles.splitModalContent) }}
        getContainer={() => document.getElementById("global-wrap")}
        closeIcon={<Icon type="cross_small" />}
      >
        {isMobile ? (
          <Spin size="large" spinning={saveFetching}>
            <CreditRequestForm
              initialValues={formInitialValues}
              isNewRequest={isNewRequest}
              fullHeight={false}
              onSubmit={handleSubmitForm}
              onClose={handleCloseModal}
              isSubmitting={creditRequestSubmitting || fetching}
              isDisabledSubmitBtn={isDisabledSubmitBtn}
              onReasonChange={setSelectedReason}
            />
          </Spin>
        ) : (
          <div className={instructionsStyles.splitModalBody}>
            <div className={instructionsStyles.formContainer}>
              <div className={cls("ant-modal-header", instructionsStyles.customModalHeader)}>
                <div className="ant-modal-title">{"Credit request"}</div>
                <ButtonComponent
                  type="button"
                  className={cls(instructionsStyles.customModalClose)}
                  onClick={handleCloseModal}
                  variant="outlined"
                  icon={<Icon type="cross_small" />}
                />
              </div>
              <Spin size="large" spinning={saveFetching}>
                <CreditRequestForm
                  initialValues={formInitialValues}
                  isNewRequest={isNewRequest}
                  fullHeight={false}
                  onSubmit={handleSubmitForm}
                  onClose={handleCloseModal}
                  isSubmitting={creditRequestSubmitting || fetching}
                  isRejected={isEnableForResubmit}
                  isDisabledSubmitBtn={isDisabledSubmitBtn}
                  onReasonChange={setSelectedReason}
                />
              </Spin>
            </div>
            <CreditRequestInstructions
              reason={selectedReason || undefined}
              paymentProcessor={instructionProcessor}
            />
          </div>
        )}
      </Modal>
    </>
  )
}
